import { StrictMode, useEffect } from "react";
import { createRoot } from "react-dom/client";
import {
  BrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import App from "@/App";
import { I18nProvider } from "@/lib/i18n/I18nProvider";
import { sentryScrubIntegration, sentryScrubOptions } from "@/lib/sentry";
import { applyCompanyFaviconHint } from "@/lib/headInjection";
import { dismissBootScreen } from "@/lib/boot-screen";
import "@/styles/index.css";

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENV ?? import.meta.env.MODE,
    release: import.meta.env.VITE_BUILD_VERSION,
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    sendDefaultPii: false,
    // The free-text scrub (Kanzlei K4-B) with the portal token rule:
    // beforeSend / beforeBreadcrumb / beforeSendTransaction / beforeSendSpan,
    // and the integration whose processEvent reaches every event type.
    // beforeSendSpan is the only hook an INP span passes — its `transaction`
    // attribute is the route, `/p/<token>` before the router has matched.
    // The spread also sets maxValueLength 8192: the SDK cuts texts to 250
    // characters before any hook runs, and a cut text loses the suffix the
    // patterns anchor on. Nothing after the spread may set one of its keys
    // (sentry.wiring.test.ts).
    ...sentryScrubOptions(),
    integrations: [
      sentryScrubIntegration(),
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false, // portal errors are terminal — wrong token won't fix itself
      staleTime: 30_000,
    },
  },
});

function AppCrash() {
  // The inline boot screen (index.html) sits above #root until PortalPage
  // takes it down — a crash before that would stay hidden under it.
  useEffect(() => dismissBootScreen(), []);
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "2rem",
        fontFamily: "system-ui, sans-serif",
        color: "#1a1a1a",
        textAlign: "center",
      }}
    >
      <div>
        <h1 style={{ fontSize: "1.25rem", marginBottom: "0.5rem" }}>
          Something went wrong.
        </h1>
        <p style={{ color: "#555" }}>Please reload the page.</p>
      </div>
    </div>
  );
}

// Tokenless `?company=` favicon hint for the shared portal host. Runs once,
// before render — it only touches <head> and no-ops without a valid hint.
applyCompanyFaviconHint(window.location.search);

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("Root element not found");

createRoot(rootEl).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<AppCrash />}>
      <I18nProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </QueryClientProvider>
      </I18nProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
